import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  ACCESS_KEY_EXPIRATIONS,
  accessKeyErrorMessage,
  employeeLabel,
  useCreateAccessKey,
} from "@/hooks/use-access-keys";
import type {
  AccessKeyExpiration,
  AccessKeyRole,
  MinimizedEmployee,
} from "@/hooks/use-access-keys";

import { EmployeeCombobox } from "./employee-combobox";
import type { IssuedKey } from "./secret-dialog";

const ROLE_DESCRIPTIONS: Record<AccessKeyRole, string> = {
  Read: "Platform Observer: read every module, change nothing.",
  "Read + Write": "Platform Admin: full read and write access.",
};

// Same rule access-keys enforces, checked here so the operator sees it inline.
const NAME_PATTERN = /^[a-zA-Z0-9\s\-_.]+$/;

interface CreateAccessKeyCardProps {
  tenantId: string;
  tenantName: string;
  /** access-keys refuses a name already used in the tenant. */
  existingNames: string[];
  onIssued: (issued: IssuedKey) => void;
}

export function CreateAccessKeyCard({
  tenantId,
  tenantName,
  existingNames,
  onIssued,
}: CreateAccessKeyCardProps) {
  const [owner, setOwner] = useState<MinimizedEmployee | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [role, setRole] = useState<AccessKeyRole>("Read");
  const [expiration, setExpiration] = useState<AccessKeyExpiration>("1 year");
  const createKey = useCreateAccessKey();

  const normalizedName = name.trim().replace(/\s+/g, " ");
  const nameError =
    normalizedName === ""
      ? null
      : !NAME_PATTERN.test(normalizedName)
        ? "Letters, digits, spaces, dashes, underscores and dots only"
        : existingNames.includes(normalizedName)
          ? "This tenant already has a key with this name"
          : null;
  const canSubmit =
    owner !== null &&
    normalizedName !== "" &&
    nameError === null &&
    !createKey.isPending;

  const handleSubmit = async () => {
    if (!owner || !canSubmit) return;
    try {
      const created = await createKey.mutateAsync({
        tenant_id: tenantId,
        employee_id: owner.id,
        name: normalizedName,
        description: description.trim() === "" ? undefined : description.trim(),
        roles: [role],
        expiration,
      });
      onIssued({
        secret: created.access_key,
        tenantName,
        name: normalizedName,
        owner: employeeLabel(owner),
        role,
        expiration,
      });
      createKey.reset();
      setOwner(null);
      setName("");
      setDescription("");
    } catch (error: unknown) {
      toast.error(accessKeyErrorMessage(error, "The key could not be created"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-5" />
          Create API key for {tenantName}
        </CardTitle>
        <CardDescription>
          The key is owned by the person you pick here, exactly as if they
          created it from their own settings. It never carries your identity.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="access-key-owner">Owner</Label>
          <EmployeeCombobox
            id="access-key-owner"
            tenantId={tenantId}
            value={owner}
            onChange={setOwner}
            disabled={createKey.isPending}
          />
          <p className="text-muted-foreground text-xs">
            A real person in the tenant who will use the key. Service accounts
            and departed employees are not offered.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="access-key-name">Name</Label>
            <Input
              id="access-key-name"
              value={name}
              placeholder="e.g. Acme API Access Key"
              maxLength={255}
              disabled={createKey.isPending}
              onChange={(event) => {
                setName(event.target.value);
              }}
              aria-invalid={nameError !== null}
              aria-describedby={nameError ? "access-key-name-error" : undefined}
            />
            {nameError ? (
              <p
                id="access-key-name-error"
                role="alert"
                className="text-destructive text-xs"
              >
                {nameError}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="access-key-expiration">Expires in</Label>
            <Select
              value={expiration}
              disabled={createKey.isPending}
              onValueChange={(value) => {
                setExpiration(value as AccessKeyExpiration);
              }}
            >
              <SelectTrigger id="access-key-expiration" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_KEY_EXPIRATIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="access-key-description">Description (optional)</Label>
          <Textarea
            id="access-key-description"
            value={description}
            placeholder="What the key is for, so it is recognisable later"
            maxLength={1000}
            rows={2}
            disabled={createKey.isPending}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </div>

        <div className="space-y-2">
          <Label id="access-key-access">Access</Label>
          <RadioGroup
            aria-labelledby="access-key-access"
            value={role}
            disabled={createKey.isPending}
            onValueChange={(value) => {
              setRole(value as AccessKeyRole);
            }}
            className="grid gap-3 md:grid-cols-2"
          >
            {Object.entries(ROLE_DESCRIPTIONS).map(([value, summary]) => (
              <label
                key={value}
                className="hover:bg-accent has-[[data-state=checked]]:border-primary flex cursor-pointer items-start gap-3 rounded-md border p-3"
              >
                <RadioGroupItem value={value} className="mt-0.5" />
                <span className="space-y-1">
                  <span className="block text-sm font-medium">{value}</span>
                  <span className="text-muted-foreground block text-xs">
                    {summary}
                  </span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </div>

        <div className="flex justify-end">
          <Button
            disabled={!canSubmit}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {createKey.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <KeyRound className="size-4" aria-hidden />
            )}
            Create key
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
